// src/hooks/useEmployeeExpandedData.js
/**
 * Custom hook for fetching comprehensive employee data for EmployeeExpandedView
 *
 * Consolidates 10+ API calls into a single hook with:
 * - Parallel data fetching for optimal performance
 * - Proper error handling with fallbacks
 * - AbortController for cleanup
 * - Comprehensive logging
 * - Memoized data transformations
 *
 * @param {string} employeeId - The employee identifier
 * @param {Object} employee - The base employee object (for fallback data)
 * @param {boolean} open - Whether the expanded view is open
 * @returns {Object} All employee data with loading and error states
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../utils/api';
import { standardizeEmployeeId, cleanJsonString } from '../utils/formatters';
import { Logger } from '../utils/logger';
import { CURRENT_DATE } from '../utils/constants';

const logger = new Logger('useEmployeeExpandedData');

/**
 * Extract engagements from client_relationships structure
 */
const extractEngagementsFromClientRelationships = (clientRelationships) => {
  const currentEngagements = [];
  const pastEngagements = [];

  if (!Array.isArray(clientRelationships)) {
    return { currentEngagements, pastEngagements };
  }

  clientRelationships.forEach((clientRel) => {
    if (clientRel.project_history && Array.isArray(clientRel.project_history)) {
      clientRel.project_history.forEach((project) => {
        const engagement = {
          project_name: project.project_name,
          client_name: clientRel.client_name,
          role: project.role,
          start_date: project.start_date,
          end_date: project.end_date,
          project_id: project.project_id || `${project.project_name}_${clientRel.client_name}`,
          client_id: clientRel.client_id,
          project_manager: project.project_manager,
          client_satisfaction_score: project.client_satisfaction_score
        };

        // Determine if current or past based on end_date
        if (!project.end_date) {
          currentEngagements.push(engagement);
        } else {
          const endDate = new Date(project.end_date);
          if (endDate > CURRENT_DATE) {
            currentEngagements.push(engagement);
          } else {
            pastEngagements.push(engagement);
          }
        }
      });
    }
  });

  return { currentEngagements, pastEngagements };
};

/**
 * Calculate role distribution from engagements
 */
const calculateRoleDistribution = (currentEngagements, pastEngagements, employeeTitle) => {
  const roleDistribution = {
    roles: {},
    hours_by_role: {},
    projects_by_role: {},
    primary_role: employeeTitle || 'Consultant'
  };

  const allEngagements = [...currentEngagements, ...pastEngagements];

  allEngagements.forEach(engagement => {
    const role = engagement.role || 'Unknown';
    roleDistribution.projects_by_role[role] = (roleDistribution.projects_by_role[role] || 0) + 1;
    // Estimate hours (assuming 3 months average per project)
    roleDistribution.hours_by_role[role] = (roleDistribution.hours_by_role[role] || 0) + 480;
  });

  // Calculate percentages for roles
  const totalProjects = Object.values(roleDistribution.projects_by_role).reduce((sum, count) => sum + count, 0);
  if (totalProjects > 0) {
    Object.keys(roleDistribution.projects_by_role).forEach(role => {
      roleDistribution.roles[role] = Math.round((roleDistribution.projects_by_role[role] / totalProjects) * 100);
    });
  }

  return roleDistribution;
};

/**
 * Extract top collaborators from client relationships
 */
const extractTopCollaborators = (clientRelationships) => {
  const collaboratorMap = {};

  if (!Array.isArray(clientRelationships)) {
    return [];
  }

  clientRelationships.forEach(clientRel => {
    if (clientRel.project_history) {
      clientRel.project_history.forEach(project => {
        if (project.project_manager && project.project_manager !== 'N/A') {
          const pmName = project.project_manager;
          if (!collaboratorMap[pmName]) {
            collaboratorMap[pmName] = {
              name: pmName,
              role: 'Project Manager',
              projects: 0,
              hours: 0
            };
          }
          collaboratorMap[pmName].projects += 1;
          collaboratorMap[pmName].hours += 480;
        }
      });
    }
  });

  return Object.values(collaboratorMap)
    .sort((a, b) => b.projects - a.projects)
    .slice(0, 5);
};

/**
 * Generate domain knowledge from employee data
 */
const generateDomainKnowledge = (employee) => {
  const domainKnowledge = [];

  // Add domains based on title
  if (employee.title?.includes('Architect')) {
    domainKnowledge.push({ name: 'Enterprise Architecture', level: 8 });
    domainKnowledge.push({ name: 'Solution Design', level: 9 });
  }

  if (employee.title?.includes('Manager')) {
    domainKnowledge.push({ name: 'Project Management', level: 8 });
    domainKnowledge.push({ name: 'Resource Planning', level: 7 });
  }

  // Add industry knowledge from active projects
  const clientIndustries = new Set();
  (employee.active_projects || []).forEach(project => {
    if (project.client_industry) {
      clientIndustries.add(project.client_industry);
    }
  });

  Array.from(clientIndustries).forEach(industry => {
    domainKnowledge.push({
      name: industry,
      level: Math.floor(Math.random() * 3) + 6
    });
  });

  // Add defaults if none found
  if (domainKnowledge.length === 0) {
    domainKnowledge.push({ name: 'Financial Services', level: 7 });
    domainKnowledge.push({ name: 'Healthcare', level: 6 });
    domainKnowledge.push({ name: 'Technology', level: 8 });
  }

  return domainKnowledge;
};

export const useEmployeeExpandedData = (employeeId, employee, open) => {
  const [loading, setLoading] = useState({
    performance: true,
    skills: true,
    engagement: true,
    career: true,
    financial: true,
    personal: true,
    reviews: true,
    compensation: true,
    potential: true,
    timesheet: true
  });

  const [data, setData] = useState({
    performance: {},
    skills: {},
    engagement: {},
    career: {},
    financial: {},
    personal: {},
    reviews: [],
    compensation: {},
    potential: {},
    timesheet: {}
  });

  const [errors, setErrors] = useState({});

  // Standardize employee ID
  const standardizedId = useMemo(() => {
    return standardizeEmployeeId(employeeId);
  }, [employeeId]);

  // Fetch performance data
  const fetchPerformanceData = useCallback(async (abortSignal) => {
    try {
      const [dirData, csatData, utilizationData, csotData] = await Promise.all([
        apiFetch(`employees/${standardizedId}/dir`, { signal: abortSignal })
          .then(response => response.ok ? response.json() : { normalized_dir: { complete: 0, operational: 0 }, dir_history: [] })
          .catch(() => ({ normalized_dir: { complete: 0, operational: 0 }, dir_history: [] })),

        apiFetch(`csat/employee/${standardizedId}`, { signal: abortSignal })
          .then(response => response.ok ? response.json() : { average: 0, scores: [] })
          .catch(() => ({ average: 0, scores: [] })),

        apiFetch(`employees/${standardizedId}/utilization-details?period=13w`, { signal: abortSignal })
          .then(response => response.ok ? response.json() : { average: 0, weekly_data: [] })
          .catch(() => ({ average: 0, weekly_data: [] })),

        apiFetch(`csot/${standardizedId}`, { signal: abortSignal })
          .then(response => response.ok ? response.json() : { metrics: { csot: { csot_4_weeks: 0, csot_8_weeks: 0, csot_12_weeks: 0 } } })
          .catch(() => ({ metrics: { csot: { csot_4_weeks: 0, csot_8_weeks: 0, csot_12_weeks: 0 } } }))
      ]);

      const performanceData = {
        dir_scores: {
          complete: dirData.normalized_dir?.complete || 0,
          operational: dirData.normalized_dir?.operational || 0
        },
        dir_history: dirData.dir_history || [],
        csat_history: csatData.scores?.map(score => ({
          date: score.date,
          score: score.score,
          target: 4.0
        })) || [],
        utilization_history: utilizationData.weekly_data?.map(week => ({
          period: new Date(week.week_ending).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          value: week.utilization,
          target: 85
        })) || [],
        csot_forecast: [
          { period: '4 Weeks', value: csotData.metrics?.csot?.csot_4_weeks || 0, target: 85 },
          { period: '8 Weeks', value: csotData.metrics?.csot?.csot_8_weeks || 0, target: 85 },
          { period: '12 Weeks', value: csotData.metrics?.csot?.csot_12_weeks || 0, target: 85 }
        ],
        team_comparison: {
          metrics: [
            { name: 'Utilization', employee: utilizationData.average || 0, team_avg: 80, dept_avg: 75 },
            { name: 'CSAT', employee: csatData.average ? csatData.average * 20 : 0, team_avg: 80, dept_avg: 75 },
            { name: 'DIR', employee: (dirData.normalized_dir?.complete || 0) * 5 + 50, team_avg: 60, dept_avg: 55 },
            { name: 'CSOT', employee: csotData.metrics?.csot?.csot_12_weeks || 0, team_avg: 85, dept_avg: 80 },
            { name: 'Revenue', employee: 75, team_avg: 70, dept_avg: 65 }
          ]
        }
      };

      setData(prev => ({ ...prev, performance: performanceData }));
      setLoading(prev => ({ ...prev, performance: false }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching performance data:', error);
        setErrors(prev => ({ ...prev, performance: error.message }));
        setLoading(prev => ({ ...prev, performance: false }));
      }
    }
  }, [standardizedId]);

  // Fetch skills data
  const fetchSkillsData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${standardizedId}/technical-skills`, { signal: abortSignal });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const skillsResponse = await response.json();

      const technicalSkills = skillsResponse.skills?.map(skill => ({
        name: skill.name,
        level: skill.score,
        trending: skill.trending,
        description: skill.description
      })) || [];

      const softSkills = (employee.skills?.soft || []).map(skill => ({
        name: skill.name,
        level: skill.score
      }));

      const discProfile = {
        d: employee.disc_dominance || 0,
        i: employee.disc_influence || 0,
        s: employee.disc_steadiness || 0,
        c: employee.disc_conscientiousness || 0
      };

      const domainKnowledge = generateDomainKnowledge(employee);

      const skillsData = {
        technical_skills: technicalSkills,
        soft_skills: softSkills,
        domain_knowledge: domainKnowledge,
        skill_growth: skillsResponse.skill_growth || [],
        skill_gaps: skillsResponse.skill_gaps?.map(gap => ({
          skill: gap.skill_name,
          current_level: gap.current_level,
          target_level: gap.target_level,
          reason: gap.reason
        })) || [],
        certifications: skillsResponse.certifications || [],
        disc: discProfile,
        strengths: employee.skills?.strengths || []
      };

      setData(prev => ({ ...prev, skills: skillsData }));
      setLoading(prev => ({ ...prev, skills: false }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching skills data:', error);
        setErrors(prev => ({ ...prev, skills: error.message }));
        setLoading(prev => ({ ...prev, skills: false }));
      }
    }
  }, [standardizedId, employee]);

  // Fetch engagement data from /full endpoint (optimized single call)
  const fetchEngagementData = useCallback(async (abortSignal) => {
    try {
      // Use /full endpoint instead of /engagement-data for consistency
      const response = await apiFetch(`employees/${employeeId}/full`, { signal: abortSignal });

      // Parse response
      let parsedData = await response.json();

      // Extract engagement data from /full response
      // /full endpoint returns: { engagement: { current_engagements: [], past_engagements: [], project_history: [] } }
      const engagementDataRaw = parsedData.engagement || {};

      // Try to use structured engagement data first, fallback to client_relationships
      let currentEngagements = engagementDataRaw.current_engagements || [];
      let pastEngagements = engagementDataRaw.past_engagements || [];

      // If no structured engagement data, extract from client_relationships (legacy format)
      if (currentEngagements.length === 0 && pastEngagements.length === 0 && parsedData.client_relationships) {
        const extracted = extractEngagementsFromClientRelationships(parsedData.client_relationships);
        currentEngagements = extracted.currentEngagements;
        pastEngagements = extracted.pastEngagements;
      }

      const roleDistribution = calculateRoleDistribution(
        currentEngagements,
        pastEngagements,
        employee.title
      );

      const topCollaborators = parsedData.client_relationships
        ? extractTopCollaborators(parsedData.client_relationships)
        : [];

      const engagementData = {
        current_engagements: currentEngagements.length > 0 ? currentEngagements :
                            (engagementDataRaw.current_engagements || parsedData.current_engagements || employee.active_projects || []),
        past_engagements: pastEngagements.length > 0 ? pastEngagements :
                         (engagementDataRaw.past_engagements || parsedData.past_engagements || employee.past_engagements || []),
        project_history: engagementDataRaw.project_history || parsedData.project_history || [],
        top_collaborators: topCollaborators.length > 0 ? topCollaborators :
                          (engagementDataRaw.collaboration_patterns || engagementDataRaw.top_collaborators || parsedData.top_collaborators || []),
        project_contributions: engagementDataRaw.project_contributions || parsedData.project_contributions || [],
        client_satisfaction: engagementDataRaw.client_satisfaction || parsedData.client_satisfaction || [],
        role_distribution: Object.keys(roleDistribution.roles).length > 0 ? roleDistribution :
                          (engagementDataRaw.role_distribution || parsedData.role_distribution || {
                            roles: employee.role_distribution?.roles || {},
                            hours_by_role: employee.role_distribution?.hours_by_role || {},
                            projects_by_role: employee.role_distribution?.projects_by_role || {},
                            primary_role: employee.title || 'Consultant'
                          })
      };

      logger.debug('Engagement data structure:', {
        engagementDataRaw,
        currentEngagementsCount: currentEngagements.length,
        pastEngagementsCount: pastEngagements.length,
        finalEngagementData: engagementData
      });

      setData(prev => ({ ...prev, engagement: engagementData }));
      setLoading(prev => ({ ...prev, engagement: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching engagement data:', error);
        setErrors(prev => ({ ...prev, engagement: error.message }));
        setData(prev => ({
          ...prev,
          engagement: {
            current_engagements: employee.active_projects || [],
            past_engagements: employee.past_engagements || [],
            top_collaborators: [],
            project_contributions: [],
            client_satisfaction: [],
            role_distribution: {
              roles: employee.role_distribution?.roles || {},
              hours_by_role: employee.role_distribution?.hours_by_role || {},
              primary_role: employee.title || 'Consultant'
            }
          }
        }));
        setLoading(prev => ({ ...prev, engagement: false }));
      }
    }
  }, [employeeId, employee]);

  // Fetch career data
  const fetchCareerData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${employeeId}/career-development`, { signal: abortSignal });
      const careerData = await response.json();
      setData(prev => ({ ...prev, career: careerData }));
      setLoading(prev => ({ ...prev, career: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching career data:', error);
        setErrors(prev => ({ ...prev, career: error.message }));
        setData(prev => ({
          ...prev,
          career: {
            salaryHistory: [],
            certifications: [],
            training: [],
            careerPath: {
              current_position: employee.title,
              path: [],
              next_roles: []
            },
            developmentPlan: {
              period: '',
              goals: []
            }
          }
        }));
        setLoading(prev => ({ ...prev, career: false }));
      }
    }
  }, [employeeId, employee?.title]);

  // Fetch financial data
  const fetchFinancialData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${employeeId}/financial-impact`, { signal: abortSignal });
      const financialData = await response.json();
      setData(prev => ({ ...prev, financial: financialData }));
      setLoading(prev => ({ ...prev, financial: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching financial data:', error);
        setErrors(prev => ({ ...prev, financial: error.message }));
        setData(prev => ({
          ...prev,
          financial: {
            revenue_generation: [],
            billable_rate_history: [],
            project_profitability: [],
            utilization_value: {}
          }
        }));
        setLoading(prev => ({ ...prev, financial: false }));
      }
    }
  }, [employeeId]);

  // Fetch personal data
  const fetchPersonalData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${employeeId}/personal-info`, { signal: abortSignal });
      const personalData = await response.json();
      setData(prev => ({ ...prev, personal: personalData }));
      setLoading(prev => ({ ...prev, personal: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching personal data:', error);
        setErrors(prev => ({ ...prev, personal: error.message }));
        setData(prev => ({
          ...prev,
          personal: {
            ...employee,
            address: {},
            phone: '',
            birth_date: '',
            education: []
          }
        }));
        setLoading(prev => ({ ...prev, personal: false }));
      }
    }
  }, [employeeId, employee]);

  // Fetch reviews data
  const fetchReviewsData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${employeeId}/performance-reviews`, { signal: abortSignal });
      const reviewsData = await response.json();
      setData(prev => ({ ...prev, reviews: reviewsData }));
      setLoading(prev => ({ ...prev, reviews: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching reviews data:', error);
        setErrors(prev => ({ ...prev, reviews: error.message }));
        setData(prev => ({ ...prev, reviews: [] }));
        setLoading(prev => ({ ...prev, reviews: false }));
      }
    }
  }, [employeeId]);

  // Fetch compensation data
  const fetchCompensationData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${employeeId}/compensation`, { signal: abortSignal });
      const compensationData = await response.json();
      setData(prev => ({ ...prev, compensation: compensationData }));
      setLoading(prev => ({ ...prev, compensation: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching compensation data:', error);
        setErrors(prev => ({ ...prev, compensation: error.message }));
        setData(prev => ({
          ...prev,
          compensation: {
            salaryHistory: [],
            marketComparison: {},
            compensationMetrics: {},
            bonusHistory: [],
            totalCompensation: {}
          }
        }));
        setLoading(prev => ({ ...prev, compensation: false }));
      }
    }
  }, [employeeId]);

  // Fetch potential data
  const fetchPotentialData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${employeeId}/potential`, { signal: abortSignal });
      const potentialData = await response.json();
      setData(prev => ({ ...prev, potential: potentialData }));
      setLoading(prev => ({ ...prev, potential: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching potential data:', error);
        setErrors(prev => ({ ...prev, potential: error.message }));
        setData(prev => ({
          ...prev,
          potential: {
            potentialData: {},
            leadershipAssessment: {},
            growthTrajectory: {},
            retentionRisk: {}
          }
        }));
        setLoading(prev => ({ ...prev, potential: false }));
      }
    }
  }, [employeeId]);

  // Fetch timesheet data
  const fetchTimesheetData = useCallback(async (abortSignal) => {
    try {
      const response = await apiFetch(`employees/${standardizedId}/timesheet-summary`, { signal: abortSignal });
      const timesheetData = await response.json();
      setData(prev => ({ ...prev, timesheet: timesheetData }));
      setLoading(prev => ({ ...prev, timesheet: false }));
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching timesheet data:', error);
        setErrors(prev => ({ ...prev, timesheet: error.message }));
        setData(prev => ({
          ...prev,
          timesheet: {
            employee_id: employeeId,
            total_billable_hours: 0,
            total_available_hours: 0,
            average_utilization: 0,
            projects_summary: [],
            weekly_timesheets: [],
            monthly_summaries: []
          }
        }));
        setLoading(prev => ({ ...prev, timesheet: false }));
      }
    }
  }, [standardizedId, employeeId]);

  // Main effect to fetch all data
  useEffect(() => {
    if (!open || !employeeId || !employee) {
      return;
    }

    const abortController = new AbortController();

    // Reset loading states
    setLoading({
      performance: true,
      skills: true,
      engagement: true,
      career: true,
      financial: true,
      personal: true,
      reviews: true,
      compensation: true,
      potential: true,
      timesheet: true
    });

    // Fetch in two waves to avoid overwhelming the browser connection pool
    // Wave 1: tabs visible first (DIR, CSOT, CSAT, Utilization, Skills, Engagements, Compensation)
    Promise.all([
      fetchPerformanceData(abortController.signal),
      fetchSkillsData(abortController.signal),
      fetchEngagementData(abortController.signal),
      fetchFinancialData(abortController.signal),
      fetchCompensationData(abortController.signal),
    ]).catch(error => {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        logger.error('Error fetching employee data (wave 1):', error);
      }
    }).then(() => {
      // Wave 2: remaining tabs (Personal Info, Time Sheets, Career, Reviews, Potential)
      if (abortController.signal.aborted) return;
      Promise.all([
        fetchPersonalData(abortController.signal),
        fetchCareerData(abortController.signal),
        fetchReviewsData(abortController.signal),
        fetchPotentialData(abortController.signal),
        fetchTimesheetData(abortController.signal),
      ]).catch(error => {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          logger.error('Error fetching employee data (wave 2):', error);
        }
      });
    });

    return () => {
      abortController.abort();
    };
  }, [
    open,
    employeeId,
    employee,
    fetchPerformanceData,
    fetchSkillsData,
    fetchEngagementData,
    fetchCareerData,
    fetchFinancialData,
    fetchPersonalData,
    fetchReviewsData,
    fetchCompensationData,
    fetchPotentialData,
    fetchTimesheetData
  ]);

  return {
    data,
    loading,
    errors
  };
};
